import type { StateAbbreviation } from '@ria-client/supergraph/marketplace/types';
import type { ProductCard } from '@ria-client/supergraph/roundup/types';
import { Skeleton } from '@ria-client/tw-shadcn/components/skeleton';
import { filterEntitiesByState } from '@ria-client/utils/geo-data';
import { getBadgeText } from './category-utils';
import { ProductCardRedesignSkeleton } from './product-card-redesign-skeleton';
import { ProductCardSkeleton } from './product-card-skeleton';
import { ProductCardsWrapper } from './product-cards-wrapper';

export interface CategorySectionProps {
  title?: string;
  description?: string;
  eyebrowText?: string;
  productCards: ProductCard[];
  productPositionBase: number;
  isSeoBankingFFActive: boolean;
  regionCode?: StateAbbreviation;
  showSkeleton?: boolean;
  isBankingProductCardsRedesignExperimentActive?: boolean;
}

export function CategorySection({
  title,
  description,
  eyebrowText,
  productCards,
  productPositionBase,
  isSeoBankingFFActive,
  regionCode,
  showSkeleton = false,
  isBankingProductCardsRedesignExperimentActive,
}: CategorySectionProps) {
  const geoTargetedCards = regionCode
    ? filterEntitiesByState(productCards, regionCode)
    : productCards;

  if (!geoTargetedCards.length) return null;

  if (showSkeleton) {
    const ProductCardSkeletonComponent = isBankingProductCardsRedesignExperimentActive
      ? ProductCardRedesignSkeleton
      : ProductCardSkeleton;
    const showTitleSkeleton =
      (title || eyebrowText) && !isBankingProductCardsRedesignExperimentActive;

    return (
      <li className="mx-auto mt-5 max-w-full md:mt-8 md:max-w-[82%] first:mt-0">
        {showTitleSkeleton && (
          <div className="mb-3 flex flex-col gap-2">
            <Skeleton className="h-5 w-[250px] rounded" />
            <Skeleton className="h-[30px] w-full rounded" />
          </div>
        )}
        {Array.from({ length: productCards.length }).map((_, index) => (
          <ProductCardSkeletonComponent key={index} ourTakeExpanded={isSeoBankingFFActive} />
        ))}
      </li>
    );
  }

  const cardBadgeText = getBadgeText(eyebrowText, title);

  return (
    <li
      className="mx-auto mb-8 flex max-w-full flex-col p-0 md:max-w-[82%]"
      data-testid="product-card-section"
    >
      {(title || eyebrowText) && !isBankingProductCardsRedesignExperimentActive && (
        <div className="mb-3">
          {eyebrowText && <p className="typography-text-bold pb-2">{eyebrowText}</p>}
          {title && <p className="typography-text-large-bold">{title}</p>}
          {description && <p className="mt-1">{description}</p>}
        </div>
      )}
      <ul className="p-0">
        {geoTargetedCards.map((card, cardIndex) => (
          <ProductCardsWrapper
            card={card}
            cardBadgeText={cardIndex === 0 ? cardBadgeText : undefined}
            cardIndex={cardIndex}
            isBankingProductCardsRedesignExperimentActive={
              isBankingProductCardsRedesignExperimentActive
            }
            isSeoBankingFFActive={isSeoBankingFFActive}
            key={cardIndex}
            productPositionBase={productPositionBase}
          />
        ))}
      </ul>
    </li>
  );
}
